import React from 'react';
import styled from 'styled-components';
import Toggle from 'react-toggle';

import 'react-toggle/style.css';
import TextComponent from '@/components/TextComponent';
import Tooltip from '@/components/Tooltip';
import './Toggle.css';
import { ReactComponent as DoctornowLogoText } from '@/assets/images/common/doctornowLogoText.svg';
import { ReactComponent as PartnerCenter } from '@/assets/images/common/partnerCenter.svg';
import { ReactComponent as Question } from '@/assets/images/layout/question.svg';
import GlobalAlert from '@/components/GlobalAlert';
import Guide from '@/components/AppLayout/containers/Guide';
import theme from '@/styles/theme';

interface Props {
  goMain: () => void;
  isLoggedIn: boolean;
  isAbsent: boolean;
  onClickBePresent: () => void;
  onClickBeAbsent: () => void;
}

const Header = (props: Props) => {
  const { goMain, isLoggedIn, isAbsent, onClickBeAbsent, onClickBePresent } =
    props;

  const handleToggle = () => {
    if (isAbsent) {
      GlobalAlert.show({
        title: '처방전 접수',
        body: '상태를 변경하시면 다시 처방전이 접수 됩니다.',
        buttons: [{ text: '확인', onClick: submitChangeOperating }],
        clickOverlayToClose: true
      });
      return;
    }

    GlobalAlert.show({
      title: '처방전 접수',
      body: '상태를 변경하면 처방전을 받을수 없습니다.',
      buttons: [{ text: '확인', onClick: submitChangeOperating }],
      clickOverlayToClose: true
    });
  };

  const submitChangeOperating = () => {
    try {
      if (!isAbsent) {
        onClickBeAbsent();
      } else {
        onClickBePresent();
      }
      GlobalAlert.hide();
    } catch (error) {
      GlobalAlert.show({
        title: '',
        body: '처방전 접수 상태 변경에 실패하였습니다.'
      });
    }
  };

  return (
    <Wrapper>
      <Title onClick={goMain}>
        <DoctornowLogoText />
        <PartnerCenter />
      </Title>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <GuideWrapper>
          <Guide />
        </GuideWrapper>
        {isLoggedIn && (
          <>
            <Operating>
              <ToggleWrapper>
                <ToggleSwitchLabel>처방전 접수</ToggleSwitchLabel>
                <label>
                  <a
                    data-tip
                    data-for="absentToggleBtn"
                    style={{ display: 'flex', alignItems: 'center' }}>
                    <CustomToggle
                      active={(!isAbsent).toString()}
                      checked={!isAbsent}
                      icons={{
                        checked: (
                          <ToggleSwitchTextOn isAbsent={isAbsent}>
                            ON
                          </ToggleSwitchTextOn>
                        ),
                        unchecked: (
                          <ToggleSwitchTextOff isAbsent={isAbsent}>
                            OFF
                          </ToggleSwitchTextOff>
                        )
                      }}
                      onChange={handleToggle}
                    />
                  </a>
                  <Tooltip
                    id="absentToggleBtn"
                    place="bottom"
                    type="dark"
                    effect="solid">
                    {isAbsent ? (
                      <>
                        처방전 접수 기능이 꺼져있습니다.
                        <br />
                        여기에서 언제든 켜고 끌 수 있습니다.
                      </>
                    ) : (
                      <>
                        처방전 접수 기능이 켜져있습니다.
                        <br />
                        여기에서 언제든 켜고 끌 수 있습니다.
                      </>
                    )}
                  </Tooltip>
                </label>
              </ToggleWrapper>
              <Info>
                <a data-tip>
                  <Question
                    style={{
                      width: 18,
                      height: 18,
                      marginLeft: 8,
                      marginTop: 6
                    }}
                  />
                </a>

                <Tooltip place="bottom" effect="solid">
                  접수를 잠시 받기 어려운 경우나,
                  <br />
                  영업 조기 종료 시 이용하세요.
                </Tooltip>
              </Info>
            </Operating>
            <Off isOperating={!isAbsent}>
              <a data-tip data-for="breakTime">
                <TextComponent
                  style={{ height: '100%' }}
                  color={'#fff'}
                  fontSize={20}
                  fontWeight={700}>
                  {`처방전 접수 기능이 꺼져있어요`}
                </TextComponent>
              </a>
              <Tooltip id="breakTime" place="bottom" effect="solid">
                {`익일 영업시간에 맞춰 'ON' 상태로 자동 변경됩니다.`}
              </Tooltip>
            </Off>
          </>
        )}
      </div>
    </Wrapper>
  );
};

export default Header;

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  position: fixed;
  background: ${theme.DF_COLOR.WHITE};
  height: 80px;
  align-items: center;
  padding: 0 40px;
  z-index: 998;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.04);
  justify-content: space-between;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;

  > * {
    &:nth-child(1) {
      margin-right: 7px;
    }
  }
`;

const GuideWrapper = styled.div`
  flex: 1;
`;

const Operating = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;
`;

const ToggleWrapper = styled.div`
  z-index: 99;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 1rem;

  > * {
    &:nth-child(1) {
      margin-right: 10px;
    }
  }

  &.react-toggle--checked .react-toggle-thumb {
    left: 37px;
    border-color: #19ab27;
  }
`;

const CustomToggle = styled(Toggle)<{ active: string }>`
  z-index: 1;
  height: 32px;
  width: 86px;

  * {
    height: 32px;
    line-height: 0;
    border-radius: 5px;
    ${({ theme }) => theme.DF_TYPOGRAPHY.UI14B};
  }

  & .react-toggle-thumb {
    left: 48px;
    margin: 3px;
    width: 44px;
    height: 24px;
    border-radius: 5px;
    border: none;
    background-color: ${({ active, theme }) =>
      active === 'true' ? theme.DF_COLOR.GREEN500 : theme.DF_COLOR.GRAY700};
    transition: all 0.25s ease;
  }
`;

const Info = styled.div`
  & div {
    text-align: center;
  }
`;

const Off = styled.div<{ isOperating: boolean }>`
  display: ${({ isOperating }) => (isOperating ? 'none' : 'flex')};
  flex-flow: column;
  width: 100%;
  position: absolute;
  background: ${theme.DF_COLOR.GRAY600};
  opacity: 0.9;
  height: 100%;
  left: 0;
  align-items: center;
  justify-content: center;

  > * {
    &:nth-child(1) {
      margin-bottom: 5px;
    }
  }
`;

const ToggleSwitchTextOn = styled.span<{ isAbsent: boolean }>`
  ${({ theme }) => theme.DF_TYPOGRAPHY.UI14B};
  color: ${({ isAbsent, theme }) =>
    !isAbsent ? theme.DF_COLOR.WHITE : theme.DF_COLOR.GRAY400};
  position: absolute;
  left: 2px;
  display: inline-block;
  height: 24px;
`;

const ToggleSwitchTextOff = styled.span<{ isAbsent: boolean }>`
  ${({ theme }) => theme.DF_TYPOGRAPHY.UI14B};
  color: ${({ isAbsent, theme }) =>
    isAbsent ? theme.DF_COLOR.WHITE : theme.DF_COLOR.GRAY400};
  position: absolute;
  right: -10px;
  display: inline-block;
  height: 24px;
`;

const ToggleSwitchLabel = styled.span`
  ${({ theme }) => theme.DF_TYPOGRAPHY.UI16M};
  color: ${({ theme }) => theme.DF_COLOR.GRAY900};
`;
